---
title: "Branding your careers page"
description: "Customize your Chosen careers page: upload a banner image, write a company description, and set your URL slug. What custom CSS and domains need."
category: "careers"
keywords:
  - "careers page branding"
  - "careers page banner"
  - "company description ATS"
  - "custom careers domain"
schema: "HowTo"
nav:
  section: "guides"
  order: 20
  parent: "/guides/careers"
---

Everything you can change about how the careers page looks lives in one place: **Settings → Organization → Career Page Settings**. It's a short list on purpose — a banner, a company description, and your URL slug. The page is clean by default, so a little goes a long way.

## What you can customize

Open **Career Page Settings** and you'll find these:

| Setting | What it does |
| --- | --- |
| **Career Page Banner** | A hero image across the top of the page. 4:1 aspect ratio, any image format, up to 5 MB. |
| **Company Description** | A formatted block shown above the job list. This is your pitch. |
| **Career Page Slug** | The `<your-org-slug>` part of your careers URL. |
| **Page Status** | Published or Hidden — covered in [publishing jobs](/guides/careers/publish-jobs). |

## Upload a banner

The banner is the first thing a candidate sees. It also stands in for your logo — if you don't upload one, the page shows your organization name as plain text instead.

<Steps>
  <li>In **Career Page Settings**, find **Career Page Banner** and click the upload area.</li>
  <li>Pick an image. If it isn't already close to a 4:1 ratio, Chosen opens a crop step so you can choose the part that matters.</li>
  <li>Confirm the crop. The banner saves and shows in the preview.</li>
</Steps>

Use **Replace** to swap it later, or **Remove** to drop back to the plain-text org name. The file has to be under 5 MB and a format a browser can render as an image.

<Callout type="warning" title="It's a wide banner, not a square logo">
The banner is cropped to 4:1 — short and wide. A square logo or a tall image will be cut down hard. Use artwork built for a wide strip, or accept the crop the editor offers you.
</Callout>

## Write the company description

The **Company Description** sits above your job list and supports formatted text — headings, bold, links, lists. It's the one place on the careers page to say who you are.

Keep it tight. Candidates came to see the jobs, not a manifesto. A few sentences on what the company does and why someone would want to work there beats three paragraphs. If you have more to say, link out to your main site.

## Set your slug

The **Career Page Slug** decides your URL: `hire.chosenhq.com/careers/<slug>`. Lowercase letters, numbers, and hyphens only. Set it before you share the page anywhere — changing it later breaks every existing link. [Publishing jobs](/guides/careers/publish-jobs) covers the consequences in full.

## Custom CSS and a custom domain

The standard settings stop at a banner, a description, and a slug. There's no theme editor, no brand-color picker, and no font control in the UI.

If you need the careers page on your own domain, or want custom styling beyond what's above, that's something Chosen can set up outside the standard settings. Get in touch and ask — it isn't self-serve, but it's possible.

## Limits

Page layout is fixed: banner on top, description, then the job list, with a small "Powered by Chosen" line in the footer. You control the content of those pieces, not their arrangement. For a careers experience that matches your site pixel-for-pixel, render your own page from the job data — see [embedding jobs elsewhere](/guides/careers/embed-and-api).
